import type { DeliveryRunDetail, Order } from '../types/order'
import { stopsInOrder } from './delivery'
import { parseApiDate, toYmd } from './format'

// Routes driven (Delivery Reports) — the derivations behind the owner's "how effective was the
// planning" overview, pure so they can be checked with a fixture. Master contract:
// ../../API/docs/frontend/pos-delivery-run-contract.md § "Routes driven — the history read".
//
// What the data can say: there is NO departure prediction (owner decision 2026-10-02: "started_at
// and actuals are enough"), so nothing here compares against an estimate. The figures are the
// real moments only — how long a finished order waited at the store before its route left, the
// time between doors, the route's time on the road, and for pre-orders the door against the
// promise. delivery_eta_at on a completed route is a short-horizon value for the API's
// calibration runbook and is never read here. Every timestamp goes through parseApiDate: an
// unreadable or null one gives a null figure, never NaN (a NaN average renders as "NaN min").

/** The API's cap on the history window (`Delivery run history window exceeds 31 days`). */
export const MAX_HISTORY_DAYS = 31

/** A pre-order counts as late when its door was reached MORE than this many minutes after
 *  `scheduled_for`. The owner's figure (confirmed 2026-10-02). */
export const LATE_AFTER_MINUTES = 5

/** Signed whole minutes from `a` to `b` (b − a); null when either is missing or unreadable. */
export const minutesBetween = (a?: string | null, b?: string | null): number | null => {
  const from = parseApiDate(a)
  const to = parseApiDate(b)
  if (!from || !to) return null
  return Math.round((to.getTime() - from.getTime()) / 60_000)
}

/** delivered = COMPLETED; cancelled = cancelled or refunded (no figures, as countedStops leaves
 *  them out of the route); other = anything else a finished route should not hold. */
export type StopSettled = 'delivered' | 'cancelled' | 'other'

export interface StopFigures {
  stop: Order
  settled: StopSettled
  /** The promised time for a pre-order (scheduled_for); null = ASAP. */
  promise: string | null
  readyAt: string | null
  deliveredAt: string | null
  /** Kitchen ready → route left (started_at − ready_at), never negative; null without ready_at. */
  waitedAtStoreMin: number | null
  /** Previous door → this door; the first stop from started_at. */
  legMin: number | null
  /** delivered_at − scheduled_for, pre-orders only: negative = early. */
  vsPromiseMin: number | null
  late: boolean
}

const settledOf = (stop: Order): StopSettled => {
  if (stop.status === 'CANCELLED' || stop.status === 'REFUNDED') return 'cancelled'
  return stop.status === 'COMPLETED' ? 'delivered' : 'other'
}

/**
 * One stop's figures. `previousDeliveredAt` is the delivered_at of the last DELIVERED stop
 * before this one (cancelled stops skipped — nobody rode there), or the route's started_at for
 * the first stop. routeFigures supplies it; a null there leaves the leg null.
 */
export const stopFigures = (
  run: Pick<DeliveryRunDetail, 'started_at'>,
  stop: Order,
  previousDeliveredAt: string | null | undefined
): StopFigures => {
  const settled = settledOf(stop)
  const promise = stop.is_preorder ? stop.scheduled_for ?? null : null
  if (settled === 'cancelled') {
    return {
      stop,
      settled,
      promise,
      readyAt: null,
      deliveredAt: null,
      waitedAtStoreMin: null,
      legMin: null,
      vsPromiseMin: null,
      late: false,
    }
  }
  const readyAt = parseApiDate(stop.ready_at) ? (stop.ready_at as string) : null
  const deliveredAt = parseApiDate(stop.delivered_at) ? (stop.delivered_at as string) : null
  const waited = minutesBetween(readyAt, run.started_at)
  // Clamped: an order marked ready after its route left (the kitchen tapped late) waited 0, not -3.
  const waitedAtStoreMin = waited === null ? null : Math.max(0, waited)
  const vsPromiseMin = promise ? minutesBetween(promise, deliveredAt) : null
  return {
    stop,
    settled,
    promise,
    readyAt,
    deliveredAt,
    waitedAtStoreMin,
    legMin: minutesBetween(previousDeliveredAt, deliveredAt),
    vsPromiseMin,
    late: vsPromiseMin !== null && vsPromiseMin > LATE_AFTER_MINUTES,
  }
}

export interface RouteFigures {
  run: DeliveryRunDetail
  departedAt: string | null
  /** The last door reached: the latest delivered_at over the stops. */
  lastDeliveredAt: string | null
  /** completed_at − started_at. */
  onRoadMin: number | null
  /** The return ESTIMATE (expected_back_at) — nothing records the real return. */
  backAt: string | null
  /** In delivery order. */
  stops: StopFigures[]
  delivered: number
  /** Delivered pre-orders — the stops a promise can be judged on. */
  preorders: number
  preordersLate: number
  avgWaitMin: number | null
}

const mean = (values: number[]): number | null =>
  values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length : null

export const routeFigures = (run: DeliveryRunDetail): RouteFigures => {
  let previousDeliveredAt: string | null = run.started_at ?? null
  const stops = stopsInOrder(run.stops).map((stop) => {
    const figures = stopFigures(run, stop, previousDeliveredAt)
    if (figures.settled === 'delivered') previousDeliveredAt = figures.deliveredAt
    return figures
  })

  let lastDeliveredAt: string | null = null
  for (const s of stops) {
    const at = parseApiDate(s.deliveredAt)
    if (at && (!lastDeliveredAt || at.getTime() > (parseApiDate(lastDeliveredAt)?.getTime() ?? 0))) {
      lastDeliveredAt = s.deliveredAt
    }
  }

  const delivered = stops.filter((s) => s.settled === 'delivered')
  const preorders = delivered.filter((s) => s.stop.is_preorder)
  return {
    run,
    departedAt: run.started_at ?? null,
    lastDeliveredAt,
    onRoadMin: minutesBetween(run.started_at, run.completed_at),
    backAt: run.expected_back_at ?? null,
    stops,
    delivered: delivered.length,
    preorders: preorders.length,
    preordersLate: preorders.filter((s) => s.late).length,
    avgWaitMin: mean(stops.flatMap((s) => (s.waitedAtStoreMin === null ? [] : [s.waitedAtStoreMin]))),
  }
}

export interface Rollup {
  routes: number
  stopsDelivered: number
  /** Mean over the routes that have an on-road time. */
  avgOnRoadMin: number | null
  /** Delivered stops per route. */
  avgStopsPerRoute: number | null
  /** Mean over EVERY stop with a wait — pooled, not a mean of route means. */
  avgWaitMin: number | null
  preorders: number
  preordersLate: number
}

export const rollup = (runs: DeliveryRunDetail[]): Rollup => {
  const routes = runs.map(routeFigures)
  const stopsDelivered = routes.reduce((sum, r) => sum + r.delivered, 0)
  return {
    routes: routes.length,
    stopsDelivered,
    avgOnRoadMin: mean(routes.flatMap((r) => (r.onRoadMin === null ? [] : [r.onRoadMin]))),
    avgStopsPerRoute: routes.length > 0 ? stopsDelivered / routes.length : null,
    avgWaitMin: mean(
      routes.flatMap((r) => r.stops.flatMap((s) => (s.waitedAtStoreMin === null ? [] : [s.waitedAtStoreMin])))
    ),
    preorders: routes.reduce((sum, r) => sum + r.preorders, 0),
    preordersLate: routes.reduce((sum, r) => sum + r.preordersLate, 0),
  }
}

/** The "No driver" bucket — a completed route lost its driver only if the user was removed. */
export const NO_DRIVER_KEY = 'none'
export const NO_DRIVER_NAME = 'No driver'

/** The driver filter's key for a route: the driver id as a string, or NO_DRIVER_KEY. */
export const driverKeyOf = (run: Pick<DeliveryRunDetail, 'driver'>): string =>
  run.driver ? String(run.driver.id) : NO_DRIVER_KEY

export interface DriverRollup extends Rollup {
  key: string
  name: string
}

/** One rollup per driver (a null driver → "No driver"), sorted by name. */
export const rollupByDriver = (runs: DeliveryRunDetail[]): DriverRollup[] => {
  const groups = new Map<string, { name: string; runs: DeliveryRunDetail[] }>()
  for (const run of runs) {
    const key = driverKeyOf(run)
    const group = groups.get(key) ?? { name: run.driver?.name ?? NO_DRIVER_NAME, runs: [] }
    group.runs.push(run)
    groups.set(key, group)
  }
  return [...groups.entries()]
    .map(([key, group]) => ({ key, name: group.name, ...rollup(group.runs) }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** "YYYY-MM-DD" of a timestamp in the terminal's zone — the day a route belongs to is the local
 *  evening it LEFT (the API windows on started_at too). null when unreadable. */
export const localDayOf = (iso?: string | null): string | null => {
  const date = parseApiDate(iso)
  return date ? toYmd(date) : null
}
